import { useQuery } from "@tanstack/react-query";
import { User } from "firebase/auth";
import { Transaction } from "../../../domain/types/Transaction";
import { TransactionFilters } from "..";
import { fetchTransactions } from "../../../domain/services/transactions/fetchTransactions";
import { useFamilyBankAccounts } from "../../../domain/services/hooks/useFamilyBankAccounts";

export const useTransactionData = (
  currentUser: User | null,
  filters: TransactionFilters,
) => {
  const { familyBankAccounts, loading } = useFamilyBankAccounts();

  const accountsKey = JSON.stringify(
    Object.keys(familyBankAccounts || {}).map((userId) => ({
      userId,
      accountIds: familyBankAccounts[userId]?.map((acc) => acc.accountId),
    })),
  );

  const { data, isLoading, error } = useQuery<{ transactions: Transaction[] }>({
    queryKey: ["transactions", currentUser?.uid, filters, accountsKey],
    queryFn: async () => {
      console.debug("Loading transactions for user:", currentUser?.uid);
      if (!currentUser?.uid || loading) {
        return { transactions: [] };
      }

      const accessToken = await currentUser.getIdToken();

      const accountIds =
        filters.member === "all"
          ? Object.fromEntries(
              Object.entries(familyBankAccounts).map(([userId, accounts]) => [
                userId,
                filters.account === "all"
                  ? accounts.map((acc) => acc.accountId)
                  : accounts
                      .filter((acc) => acc.accountId === filters.account)
                      .map((acc) => acc.accountId),
              ]),
            )
          : {
              [filters.member]:
                filters.account === "all"
                  ? familyBankAccounts[filters.member]?.map(
                      (acc) => acc.accountId,
                    ) || []
                  : [filters.account],
            };

      return fetchTransactions(accessToken, accountIds, filters.type, {
        limit: 50,
        cursor: null,
        sortBy: "date",
        sortOrder: "desc",
      });
    },
    enabled: !!currentUser?.uid && !loading,
  });

  return {
    transactions: data?.transactions || [],
    isLoading,
    error,
  };
};
